import {
  getAttachedQuestionIds,
  extractQuestionnaireId,
  type Widget as RRWidget,
} from '@seek/rr-caja';
import type { ValidationResult } from '@seek/rr-caja/src/components/types';
import { useLanguage, useTranslations } from '@vocab/react';
import { Heading, Stack } from 'braid-design-system';
import React, { type ReactNode, useRef, useState } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { scroller } from 'react-scroll';

import { EMAIL_VERIFICATION_ERROR } from 'src/app/shared/constants/errorMessages';
import {
  SECTION_JOB_AD_REFERENCE,
  SECTION_LINKOUT,
} from 'src/app/shared/constants/pageSectionName';
import {
  PAGE_ID_MANAGE,
  PAGE_ID_WRITE,
} from 'src/app/shared/constants/pageUid';
import {
  FEATURE_LINKOUT,
  useFeatureToggleData,
} from 'src/app/shared/featureToggle';
import { getAuNzOnlyCountryCode } from 'src/app/utils/country';
import { getRoleRequirementsConfig } from 'src/app/utils/getRoleRequirementsConfig';
import handleValidation from 'src/app/utils/handleValidation';
import { log } from 'src/app/utils/logging';
import { EVENT_NAME_JOB_AD_POSTED_WITH_RR } from 'src/app/v1/analytics';
import { actionUpdateRrFailedToSave } from 'src/app/v1/state/modules/session/rrFailedToSave';
import { actionUpdateJobAdPostedWithRR } from 'src/app/v1/state/modules/tealium/jobAdPostedWithRR';
import { actionUpdateRRNumberOfQuestions } from 'src/app/v1/state/modules/tealium/rrNumberOfQuestions';
import { actionUpdateRRQuestionIds } from 'src/app/v1/state/modules/tealium/rrQuestionIds';
import AdExpiryDate from 'src/app/v1/views/components/AdExpiryDate';
import { Form } from 'src/app/v1/views/components/Form';
import { JobAdReference } from 'src/app/v1/views/components/JobAdReference';
import { LinkOut } from 'src/app/v1/views/components/LinkOut';
import { PageButtons } from 'src/app/v1/views/components/PageButtons';
import { RoleRequirements } from 'src/app/v1/views/components/RoleRequirements';
import { withDesktopEnhancers } from 'src/app/v1/views/enhancers/withDesktopEnhancers';
import { useHandleEmailVerification } from 'src/app/v1/views/hooks/useHandleEmailVerification';
import { useIsApplyViaSeek } from 'src/app/v1/views/hooks/useIsApplyViaSeek';
import PageLayout from 'src/app/v1/views/layouts/PageLayout';
import { PageSection } from 'src/app/v1/views/sections/PageSection';
import { HintText } from 'src/app/v1/views/shared/HintText';
import { HirerType } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';
import type {
  ApplicationUrlRef,
  JobAdReferenceRef,
} from 'src/types/formFields';
import { ApplyViaOptions } from 'src/types/linkOut';
import type {
  CreateFlowPageContainer,
  CreateFlowPageProps,
} from 'src/types/pages';

import translations from './.vocab';

interface Props extends CreateFlowPageProps {
  handleEmailVerificationFailed: () => void;
}

const ManagePage = ({
  handleDispatchDraft,
  handleEmailVerificationFailed,
  handleNextPageRoute,
  handleProgrammaticRouteChange,
  handleSaveDraftFailed,
  pageErrorMessage,
}: Props) => {
  const { t } = useTranslations(translations);
  const {
    advertiserId,
    countryCode,
    hasAgent,
    isGov,
    linkOutApplyVia,
    questionnaireUrl,
  } = useSelector(
    (state: AppState) => ({
      advertiserId: state.session.advertiserId,
      countryCode: getAuNzOnlyCountryCode(
        state.session.hirerAccount.nation.toLowerCase(),
      ),
      hasAgent: state.session.hirerAccount.hasAgent,
      isGov: state.session.hirerAccount.hirerType === HirerType.GOVERNMENT,
      linkOutApplyVia: state.formData.linkOutApplyVia,
      questionnaireUrl: state.formData.questionnaireUrl,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();

  const applicationUrlRef = useRef<ApplicationUrlRef>(null);
  const jobAdReferenceRef = useRef<JobAdReferenceRef>(null);
  const linkOutJobAdReferenceRef = useRef<JobAdReferenceRef>(null);
  const roleRequirementRef = useRef<RRWidget>(null);

  const containerComponentMapping: CreateFlowPageContainer[] = [
    {
      containerId: SECTION_LINKOUT,
      components: [applicationUrlRef, linkOutJobAdReferenceRef],
    },
    {
      containerId: SECTION_JOB_AD_REFERENCE,
      components: [jobAdReferenceRef],
    },
  ];

  const hasLinkOutEnabled = useFeatureToggleData(FEATURE_LINKOUT);

  const isApplyViaSeek = useIsApplyViaSeek();
  const { handleEmailVerification } = useHandleEmailVerification();

  const [isLoading, setIsLoading] = useState(false);

  const { language } = useLanguage();

  const getQuestions = async () => {
    const roleRequirementsConfig = getRoleRequirementsConfig(
      advertiserId,
      language,
    );

    const questions = await getAttachedQuestionIds(
      extractQuestionnaireId(questionnaireUrl) ?? '',
      roleRequirementsConfig,
    );

    return questions;
  };

  const goToError = (errorIdentifiers: string[]) => {
    const errorIdentifier = errorIdentifiers[0];

    scroller.scrollTo(errorIdentifier, {
      duration: 500,
      delay: 100,
      smooth: true,
    });
  };

  const handlePageValidation = async (skipIfNull = false) => {
    setIsLoading(true);

    if (await handleEmailVerification()) {
      let result = handleValidation(containerComponentMapping, skipIfNull);

      if (isApplyViaSeek && (!result || result.length === 0)) {
        result = await handleRoleRequirementValidation(skipIfNull);
      }

      setIsLoading(false);

      return result;
    }

    setIsLoading(false);

    handleEmailVerificationFailed();

    return [EMAIL_VERIFICATION_ERROR];
  };

  const handleValidationForNavigation = async (skipIfNull = false) => {
    const result = await handlePageValidation(skipIfNull);

    if (result && result[0] === EMAIL_VERIFICATION_ERROR) {
      return null;
    }
    return result;
  };

  const handleRoleRequirementValidation = async (skipIfNull = false) => {
    if (!roleRequirementRef.current) {
      return null;
    }

    if (skipIfNull) {
      try {
        await roleRequirementRef.current.saveDraft();
      } catch (error) {
        log.error('Manage Page | RoleRequirement saveDraft', {
          error,
          advertiserId,
          countryCode,
          hasAgent,
          hasLinkOutEnabled,
          isGov,
          linkOutApplyVia,
          questionnaireUrl,
        });
      }

      return null;
    }

    const rrValidationResult: ValidationResult =
      roleRequirementRef.current.valid();

    if (!rrValidationResult.valid) {
      goToError(rrValidationResult.errorIdentifier);
    }

    try {
      dispatch(actionUpdateRrFailedToSave(false));
      await roleRequirementRef.current.save();

      const questions = await getQuestions();

      if (questions && questions.length > 0) {
        dispatch(
          actionUpdateJobAdPostedWithRR(EVENT_NAME_JOB_AD_POSTED_WITH_RR),
        );
        dispatch(actionUpdateRRNumberOfQuestions(questions.length));
        dispatch(actionUpdateRRQuestionIds(questions.join(',')));
      }

      return null;
    } catch (error: any) {
      if (error && error.blocksUserFlow) {
        // Validation failed
        return error.message;
      }

      // For other errors, we do not block the posting flow.
      log.error('Manage page | RoleRequirement save', {
        error,
        advertiserId,
        countryCode,
        hasAgent,
        hasLinkOutEnabled,
        isGov,
        linkOutApplyVia,
        questionnaireUrl,
      });
      dispatch(actionUpdateRrFailedToSave(true));

      return null;
    }
  };

  const onBackButtonClicked = () => {
    handleProgrammaticRouteChange(PAGE_ID_WRITE);
  };

  const renderJobAdReference = linkOutApplyVia === ApplyViaOptions.SEEK_WEBSITE;

  const shouldShowExpiryDate = hasAgent || isGov;

  return (
    <PageLayout
      hasAside
      hasProgressBar
      handleProgrammaticRouteChange={handleProgrammaticRouteChange}
      handleValidation={handleValidationForNavigation}
      handleDispatchDraft={handleDispatchDraft}
      pageErrorMessage={pageErrorMessage}
    >
      <Stack space="medium">
        <Heading
          level="3"
          component="h2"
          data={{ testid: 'manage-page-heading' }}
        >
          {t('manage-page-heading', {
            Hint: (children: ReactNode) => (
              <HintText isHeading text={children!.toString()} />
            ),
          })}
        </Heading>
        <Form
          handleNextPageRoute={handleNextPageRoute}
          handleValidation={handlePageValidation}
        >
          <Stack space="medium">
            {hasLinkOutEnabled && (
              <PageSection
                heading={t('manage-page-applications-section-title')}
                sectionName={SECTION_LINKOUT}
              >
                <LinkOut
                  applicationUrlRef={applicationUrlRef}
                  jobAdReferenceRef={jobAdReferenceRef}
                />
              </PageSection>
            )}

            {shouldShowExpiryDate && (
              <PageSection heading={t('manage-page-ad-expiry-section-title')}>
                <AdExpiryDate />
              </PageSection>
            )}

            <Stack
              space="none"
              data={{ intercomid: 'roleRequirementsSection' }}
            >
              <RoleRequirements rrWidgetRef={roleRequirementRef} />
            </Stack>

            {renderJobAdReference && (
              <PageSection sectionName={SECTION_JOB_AD_REFERENCE}>
                <JobAdReference ref={jobAdReferenceRef} />
              </PageSection>
            )}

            <PageButtons
              isLoading={isLoading}
              showBackButton={true}
              onBackButtonClicked={onBackButtonClicked}
              handleSaveDraftFailed={handleSaveDraftFailed}
              handleValidation={handlePageValidation}
              showPreviewButton={true}
            />
          </Stack>
        </Form>
      </Stack>
    </PageLayout>
  );
};

export default withDesktopEnhancers(PAGE_ID_MANAGE)(ManagePage);
